import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  getFeedStatus, listMatches, listWebhookDeliveries, me, previewToken, publishOfficial,
  type PortalMatch,
} from '../api/portal';
import { listHighlights } from '../api/clips';
import { MatchupLabel } from '../components/TeamCrest';
import { copyText } from '../lib/clipboard';
import { storySnippet } from '../lib/embedSnippets';
import { useT } from '../lib/i18n';
import { hasClips, hasOffice, isPressLogin } from '../lib/portalRole';
type StatusFilter = 'upcoming' | 'live' | 'ft' | 'all';

const PAGE_SIZE = 25;

function initialStatus(params: URLSearchParams): StatusFilter {
  const v = params.get('view');
  if (v === 'upcoming' || v === 'live' || v === 'ft' || v === 'all') return v;
  return 'upcoming';
}

function pageFromParams(params: URLSearchParams): number {
  const n = Number.parseInt(params.get('page') ?? '1', 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function pageButtons(current: number, total: number): Array<number | '…'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, 2, total - 1, total, current - 1, current, current + 1]);
  const nums = [...set].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out: Array<number | '…'> = [];
  for (const n of nums) {
    if (out.length > 0) {
      const prev = out[out.length - 1];
      if (typeof prev === 'number' && n - prev > 1) out.push('…');
    }
    out.push(n);
  }
  return out;
}

function isLive(status: string): boolean {
  return status.startsWith('in_play') || status === 'break' || status === 'suspended';
}

function isFinished(status: string): boolean {
  return status === 'finished' || status === 'abandoned' || status === 'cancelled';
}

function matchBucket(m: PortalMatch): Exclude<StatusFilter, 'all'> {
  if (isLive(m.status)) return 'live';
  if (isFinished(m.status)) return 'ft';
  return 'upcoming';
}

const WIDGET_SCRIPT = 'http://127.0.0.1:5176/dp-widgets.js';
const WIDGET_PREVIEW = 'http://127.0.0.1:5176/examples/preview.html';

function coverageLabel(raw?: string, sport?: string) {
  const v = (raw ?? 'T2').toUpperCase();
  const tennis = (sport ?? '').toLowerCase() === 'tennis';
  const basketball = (sport ?? '').toLowerCase() === 'basketball';
  if (v === 'T1') return tennis || basketball ? 'T1 · Scoreboard' : 'T1 · Broadcast';
  if (v === 'T3') return 'T3 · Full';
  return 'T2 · Standard';
}

function isLocalHost(): boolean {
  if (typeof window === 'undefined') return false;
  return /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
}

function embedSnippet(
  matchId: string,
  competition: string,
  lang: string,
  theme: string,
  skin = 'classic',
  coverage = 'T1',
  previewKey?: string,
) {
  const local = isLocalHost();
  const apiBase = local ? 'http://127.0.0.1:8080/v1' : 'https://api.origo.rs/v1';
  const wsBase = local ? 'ws://127.0.0.1:8080/v1/stream' : 'wss://ws.origo.rs/v1/stream';
  const widgetSrc = local ? WIDGET_SCRIPT : 'https://widgets.origo.rs/dp-widgets.js';
  const key = local && previewKey ? previewKey : 'YOUR_WIDGET_KEY';
  return `<script>
  window.__DP_API_BASE = '${apiBase}';
  window.__DP_WS_BASE  = '${wsBase}';
</script>
<script src="${widgetSrc}"></script>
<div
  data-dp-widget="match-tracker"
  data-dp-api-key="${key}"
  data-dp-match-id="${matchId}"
  data-dp-lang="${lang}"
  data-dp-theme="${theme}"
  data-dp-skin="${skin}"
  data-dp-coverage="${coverage}"
></div>
<div
  data-dp-widget="live-score"
  data-dp-api-key="${key}"
  data-dp-competition="${competition}"
  data-dp-lang="${lang}"
  data-dp-theme="${theme}"
  data-dp-skin="${skin}"
></div>
<div
  data-dp-widget="lineups"
  data-dp-api-key="${key}"
  data-dp-match-id="${matchId}"
  data-dp-lang="${lang}"
  data-dp-theme="${theme}"
  data-dp-skin="${skin}"
></div>
<div
  data-dp-widget="standings"
  data-dp-api-key="${key}"
  data-dp-competition="${competition}"
  data-dp-lang="${lang}"
  data-dp-theme="${theme}"
  data-dp-skin="${skin}"
></div>
<div
  data-dp-widget="fixtures"
  data-dp-api-key="${key}"
  data-dp-competition="${competition}"
  data-dp-lang="${lang}"
  data-dp-theme="${theme}"
  data-dp-skin="${skin}"
></div>
<div
  data-dp-widget="team-fixtures"
  data-dp-api-key="${key}"
  data-dp-team-code="CZV"
  data-dp-sport="football"
  data-dp-lang="${lang}"
  data-dp-theme="${theme}"
  data-dp-skin="${skin}"
></div>
<div
  data-dp-widget="player-card"
  data-dp-api-key="${key}"
  data-dp-person-id="77777777-0000-0000-0001-000000000010"
  data-dp-lang="${lang}"
  data-dp-theme="${theme}"
  data-dp-skin="${skin}"
></div>
<div
  data-dp-widget="match-card"
  data-dp-api-key="${key}"
  data-dp-match-id="${matchId}"
  data-dp-lang="${lang}"
  data-dp-theme="${theme}"
  data-dp-skin="${skin}"
></div>
<div
  data-dp-widget="leaders"
  data-dp-api-key="${key}"
  data-dp-competition="${competition}"
  data-dp-sport="football"
  data-dp-stat="goals"
  data-dp-lang="${lang}"
  data-dp-theme="${theme}"
  data-dp-skin="${skin}"
></div>
<div
  data-dp-widget="clip-reel"
  data-dp-api-key="${key}"
  data-dp-match-id="${matchId}"
  data-dp-aspect="auto"
  data-dp-lang="${lang}"
  data-dp-theme="${theme}"
  data-dp-skin="${skin}"
></div>
<div
  data-dp-widget="clip-story"
  data-dp-api-key="${key}"
  data-dp-match-id="${matchId}"
  data-dp-lang="${lang}"
  data-dp-theme="${theme}"
  data-dp-skin="${skin}"
></div>
<div
  data-dp-widget="shot-map"
  data-dp-api-key="${key}"
  data-dp-match-id="${matchId}"
  data-dp-lang="${lang}"
  data-dp-theme="${theme}"
  data-dp-skin="${skin}"
></div>
<div
  data-dp-widget="goal-bug"
  data-dp-api-key="${key}"
  data-dp-match-id="${matchId}"
  data-dp-lang="${lang}"
  data-dp-theme="${theme}"
  data-dp-skin="${skin}"
></div>
<div
  data-dp-widget="results"
  data-dp-api-key="${key}"
  data-dp-competition="${competition}"
  data-dp-sport="football"
  data-dp-lang="${lang}"
  data-dp-theme="${theme}"
  data-dp-skin="${skin}"
></div>
<div
  data-dp-widget="team-form"
  data-dp-api-key="${key}"
  data-dp-team-code="CZV"
  data-dp-sport="football"
  data-dp-lang="${lang}"
  data-dp-theme="${theme}"
  data-dp-skin="${skin}"
></div>`;
}

function previewSrc(matchId: string, competition: string, lang: string, theme: string, skin: string, key: string) {
  const q = new URLSearchParams({ match: matchId, competition, lang, theme, skin, key });
  return `${WIDGET_PREVIEW}?${q.toString()}`;
}

export function MatchesPage() {
  const { t } = useT();
  const [params, setParams] = useSearchParams();
  const matches = useQuery({ queryKey: ['portal-matches'], queryFn: listMatches, refetchInterval: 20_000 });
  const feed = useQuery({ queryKey: ['portal-feed-status'], queryFn: getFeedStatus, refetchInterval: 15_000 });
  const profile = useQuery({ queryKey: ['portal-me'], queryFn: me });
  const tokenQ = useQuery({
    queryKey: ['portal-preview-token'],
    queryFn: previewToken,
    staleTime: 20 * 60_000,
    retry: 1,
  });
  const [openId, setOpenId] = useState<string | null>(null);
  const [lang, setLang] = useState<'sr' | 'en'>('sr');
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [skin, setSkin] = useState('classic');
  const [copied, setCopied] = useState(false);
  const [copiedStory, setCopiedStory] = useState<string | null>(null);
  const status = initialStatus(params);
  const page = pageFromParams(params);
  const press = isPressLogin(profile.data);
  const setStatus = useCallback((code: StatusFilter) => {
    const next = new URLSearchParams(params);
    if (code === 'upcoming') next.delete('view');
    else next.set('view', code);
    next.delete('page');
    setParams(next, { replace: true });
  }, [params, setParams]);
  const setPage = useCallback((p: number) => {
    const next = new URLSearchParams(params);
    if (p <= 1) next.delete('page');
    else next.set('page', String(p));
    setParams(next, { replace: true });
  }, [params, setParams]);
  useEffect(() => {
    if (press && status !== 'ft' && status !== 'all') setStatus('ft');
  }, [press, status, setStatus]);

  const selected = useMemo(
    () => (matches.data ?? []).find((x) => x.id === openId) ?? null,
    [matches.data, openId],
  );

  const counts = useMemo(() => {
    const all = matches.data ?? [];
    return {
      upcoming: all.filter((m) => matchBucket(m) === 'upcoming').length,
      live: all.filter((m) => matchBucket(m) === 'live').length,
      ft: all.filter((m) => matchBucket(m) === 'ft').length,
      all: all.length,
    };
  }, [matches.data]);

  const rows = useMemo(() => {
    const all = [...(matches.data ?? [])];
    const filtered = status === 'all' ? all : all.filter((m) => matchBucket(m) === status);
    const dir = status === 'ft' ? -1 : 1;
    return filtered.sort((a, b) => {
      const d = new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime();
      return d * dir;
    });
  }, [matches.data, status]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paged = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, safePage]);
  const from = rows.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const to = Math.min(safePage * PAGE_SIZE, rows.length);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage, setPage]);

  useEffect(() => {
    setCopied(false);
  }, [openId, lang, theme]);

  const previewKey = tokenQ.data?.key ?? '';
  const inbox = useQuery({
    queryKey: ['portal-deliveries'],
    queryFn: listWebhookDeliveries,
    enabled: !!selected,
  });
  const reels = useQuery({
    queryKey: ['portal-highlights'],
    queryFn: listHighlights,
    enabled: !!selected,
  });
  const lastDelivery = (inbox.data ?? []).find((d) => d.match_id === selected?.id);
  const lastReel = (reels.data ?? []).find((r) => r.match_id === selected?.id);

  return (
    <div>
      <PortalHero
        company={profile.data?.company_name}
        live={counts.live}
        upcoming={counts.upcoming}
        sub={t('matches.sub')}
        rounds={hasOffice(profile.data)}
      />
      {!press && (
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard label={t('stat.live')} hint={t('stat.liveHint')} value={counts.live} tone={counts.live > 0 ? 'live' : 'idle'} active={status === 'live'} onClick={() => setStatus('live')} />
          <StatCard label={t('stat.upcoming')} hint={t('stat.upcomingHint')} value={counts.upcoming} tone="accent" active={status === 'upcoming'} onClick={() => setStatus('upcoming')} />
          <StatCard label={t('stat.ft')} hint={t('stat.ftHint')} value={counts.ft} active={status === 'ft'} onClick={() => setStatus('ft')} />
          <StatCard label={t('stat.all')} hint={t('stat.allHint')} value={counts.all} active={status === 'all'} onClick={() => setStatus('all')} />
        </div>
      )}
      {feed.data && !press && <FeedStrip feed={feed.data} t={t} />}
      {profile.data && (
        <div className="mb-5 flex flex-wrap items-center gap-1.5 text-[12px]">
          <span className="mr-1 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-subtle/70">{t('matches.license')}</span>
          <LicenseChip label={t('matches.sports')} value={(profile.data.allowed_sports ?? []).length ? profile.data.allowed_sports.join(', ') : 'all'} />
          <LicenseChip
            label={t('matches.competitions')}
            value={(profile.data.allowed_competitions ?? []).length
              ? profile.data.allowed_competitions!.map((c) => c.code || c.name).join(', ')
              : (profile.data.allowed_competition_ids ?? []).length
                ? `${profile.data.allowed_competition_ids.length}`
                : 'all'}
          />
          <LicenseChip label={t('matches.delay')} value={`${profile.data.delivery_delay_ms} ms`} />
          {(profile.data.allowed_teams ?? []).length > 0 && (
            <LicenseChip label={t('matches.club')} value={profile.data.allowed_teams!.map((team) => team.name).join(', ')} />
          )}
          {profile.data.parent_company_name && (
            <LicenseChip label={t('matches.league')} value={profile.data.parent_company_name} />
          )}
          {!profile.data.parent_client_id && !press && (
            <Link to="/clubs" className="ml-1 rounded-full px-2 py-0.5 font-medium text-accent hover:bg-accent/10">{t('matches.clubPortals')} →</Link>
          )}
        </div>
      )}
      <div className="card overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 border-b border-white/[0.06] px-4 py-3">
        <div className="inline-flex flex-wrap gap-0.5 rounded-xl bg-white/[0.03] p-1 ring-1 ring-inset ring-white/[0.07]">
          {(press
            ? ([['ft', t('matches.ft')], ['all', t('matches.all')]] as const)
            : ([
                ['upcoming', t('matches.upcoming')],
                ['live', t('matches.live')],
                ['ft', t('matches.ft')],
                ['all', t('matches.all')],
              ] as const)
          ).map(([code, label]) => (
            <button
              key={code}
              type="button"
              onClick={() => setStatus(code)}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition',
                status === code ? 'bg-white/[0.1] text-white shadow-sm' : 'text-subtle hover:text-white',
              )}
            >
              {code === 'live' && counts.live > 0 && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />}
              {label}
              <span className={clsx(
                'min-w-[1.25rem] rounded-full px-1.5 text-center font-mono text-[10px] leading-4 tabular-nums',
                status === code ? 'bg-accent text-[#0b1220]' : 'bg-white/[0.07] text-slate-300',
              )}>{counts[code]}</span>
            </button>
          ))}
        </div>
        <span className="ml-auto text-xs tabular-nums text-slate-500">
          {rows.length === 0
            ? t('matches.shownNone')
            : t('matches.shown', { from, to, total: rows.length })}
        </span>
      </div>
      <div className="overflow-x-auto px-1">
        <table className="table">
          <thead>
            <tr>
              <th>{t('matches.kickoff')}</th>
              <th>{t('matches.match')}</th>
              <th>{t('matches.competition')}</th>
              <th>{t('matches.coverage')}</th>
              <th>{t('matches.score')}</th>
              <th>{t('matches.status')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {paged.map((m) => (
              <tr key={m.id}>
                <td className="whitespace-nowrap tabular-nums text-slate-400">
                  {new Date(m.scheduled_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </td>
                <td className="font-medium text-white">
                  <MatchupLabel home={m.home} away={m.away} homeLogo={m.home_logo_url} awayLogo={m.away_logo_url} />
                </td>
                <td className="text-slate-400">{m.competition}</td>
                <td>
                  <span className="rounded-full bg-white/5 px-2 py-0.5 text-[11px] font-semibold text-slate-200" title={m.last_event_at ? `Last event ${m.last_event_at}` : 'No events yet'}>
                    {coverageLabel(m.coverage, m.sport_code)}
                    {m.last_sequence != null ? ` · #${m.last_sequence}` : ''}
                  </span>
                </td>
                <td>
                  <span className={clsx(
                    'inline-flex min-w-[3.25rem] justify-center rounded-md px-2 py-0.5 font-mono text-[13px] font-semibold tabular-nums',
                    isLive(m.status) ? 'bg-emerald-500/[0.1] text-emerald-200' : 'bg-white/[0.04] text-white',
                  )}>{m.score_home}–{m.score_away}</span>
                </td>
                <td>
                  {isLive(m.status)
                    ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/[0.12] px-2 py-0.5 text-[11px] font-semibold text-emerald-300 ring-1 ring-inset ring-emerald-400/25">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
                        {t('matches.live')}
                      </span>
                    )
                    : isFinished(m.status)
                      ? <span className="inline-flex items-center rounded-full bg-white/[0.04] px-2 py-0.5 text-[11px] font-semibold text-slate-300 ring-1 ring-inset ring-white/10">{t('matches.ft')}</span>
                      : <span className="inline-flex items-center rounded-full bg-accent/[0.1] px-2 py-0.5 text-[11px] font-semibold text-[#a9c6ff] ring-1 ring-inset ring-accent/25">{t('matches.scheduled')}</span>}
                </td>
                <td className="flex flex-wrap gap-1">
                  {!press && (
                    <button className="btn-secondary text-xs" onClick={() => setOpenId(m.id)}>{t('matches.embed')}</button>
                  )}
                  <Link to={`/matches/${m.id}/report`} className="btn-ghost text-xs">{t('matches.zapisnik')}</Link>
                  {m.official && (
                    <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-white">
                      {t('matches.official')}{m.official_at ? ` · ${m.official_at.slice(11, 16)}` : ''}
                    </span>
                  )}
                  {hasOffice(profile.data) && !m.official && isFinished(m.status) && !profile.data?.parent_client_id && (
                    <PublishBtn id={m.id} holds={m.qc_holds ?? 0} t={t} />
                  )}
                  {isFinished(m.status) && (
                    <>
                      {hasClips(profile.data) && (
                        <Link to={`/videos?create=1&match=${m.id}`} className="btn-ghost text-xs">{t('matches.highlight')}</Link>
                      )}
                      <button
                        type="button"
                        className="btn-ghost text-xs"
                        onClick={() => {
                          void copyText(storySnippet(m.id)).then(() => {
                            setCopiedStory(m.id);
                            setTimeout(() => setCopiedStory(null), 1500);
                          });
                        }}
                      >
                        {copiedStory === m.id ? t('matches.copied') : t('matches.copyStory')}
                      </button>
                    </>
                  )}
                  {(m.qc_holds ?? 0) > 0 && !press && (
                    <span className="text-[11px] text-amber-300">{t('matches.qc')} {m.qc_holds}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        {matches.isLoading && (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-white/[0.03]" />)}
          </div>
        )}
        {matches.data && matches.data.length === 0 && (
          <div className="empty">{t('matches.empty')}</div>
        )}
        {matches.data && matches.data.length > 0 && rows.length === 0 && (
          <div className="empty">{t('matches.emptyFilter')}</div>
        )}
        {rows.length > PAGE_SIZE && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-white/[0.06] px-4 py-3">
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={safePage <= 1}
              onClick={() => setPage(safePage - 1)}
            >
              {t('matches.prev')}
            </button>
            <div className="flex flex-wrap items-center gap-1">
              {pageButtons(safePage, pageCount).map((item, i) => (
                item === '…' ? (
                  <span key={`e${i}`} className="px-1 text-xs text-slate-600">…</span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setPage(item)}
                    className={item === safePage
                      ? 'rounded-full bg-accent px-2.5 py-1 text-xs font-semibold text-white'
                      : 'rounded-full px-2.5 py-1 text-xs font-semibold text-subtle ring-1 ring-white/10 hover:text-white'}
                  >
                    {item}
                  </button>
                )
              ))}
            </div>
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={safePage >= pageCount}
              onClick={() => setPage(safePage + 1)}
            >
              {t('matches.next')}
            </button>
          </div>
        )}
      </div>

      {selected && !press && (
        <div className="card p-4 mt-4">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h2 className="font-semibold text-white">{t('matches.embedTitle')}</h2>
            <div className="flex gap-2 flex-wrap">
              <select className="select w-auto" value={lang} onChange={(e) => setLang(e.target.value as 'sr' | 'en')}>
                <option value="sr">Srpski</option>
                <option value="en">English</option>
              </select>
              <select className="select w-auto" value={theme} onChange={(e) => setTheme(e.target.value as 'dark' | 'light')}>
                <option value="dark">Dark</option>
                <option value="light">Light</option>
              </select>
              <select className="select w-auto" value={skin} onChange={(e) => setSkin(e.target.value)}>
                <option value="classic">classic</option>
                <option value="news">news</option>
                <option value="scoreboard">scoreboard</option>
                <option value="minimal">minimal</option>
                <option value="stripe">stripe</option>
              </select>
              <button
                className="btn-primary text-xs"
                onClick={() => {
                  void navigator.clipboard.writeText(
                    embedSnippet(selected.id, selected.competition_code, lang, theme, skin, 'T1', previewKey),
                  );
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
              >
                {copied ? t('matches.copied') : t('matches.copy')}
              </button>
              <button className="btn-secondary text-xs" onClick={() => setOpenId(null)}>{t('matches.close')}</button>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-2 text-[11px]">
            <span className="uppercase tracking-wide text-slate-500">{t('matches.delivery')}</span>
            <span className="rounded-full bg-white/5 px-2 py-0.5 text-slate-300 ring-1 ring-white/10">
              {t('matches.lastAlert')} {lastDelivery
                ? `${lastDelivery.event_type} · ${lastDelivery.created_at.replace('T', ' ').slice(0, 16)}`
                : '-'}
            </span>
            <span className="rounded-full bg-white/5 px-2 py-0.5 text-slate-300 ring-1 ring-white/10">
              {t('matches.lastReel')} {lastReel
                ? `${lastReel.name} · ${lastReel.render_status}`
                : '-'}
            </span>
            <Link to="/webhooks" className="text-accent hover:underline">{t('matches.inbox')}</Link>
          </div>

          <div className="mb-4">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
              {t('matches.preview')} · {selected.home} vs {selected.away}
              {tokenQ.isPending && ' · issuing preview key…'}
            </div>
            <div className="rounded-lg overflow-hidden border border-border bg-base">
              {previewKey ? (
                <iframe
                  key={`${selected.id}-${lang}-${theme}-${skin}-${previewKey}`}
                  title="Widget preview"
                  src={previewSrc(selected.id, selected.competition_code || 'RS-SL', lang, theme, skin, previewKey)}
                  className="w-full border-0"
                  style={{ height: 640, background: theme === 'dark' ? '#030712' : '#f1f5f9' }}
                />
              ) : (
                <div className="p-6 text-sm text-slate-500">
                  {tokenQ.isError ? 'Could not issue a preview key. Check that the gateway is running.' : 'Preparing preview…'}
                </div>
              )}
            </div>
          </div>

          <pre className="text-xs text-slate-300 whitespace-pre-wrap">
            {embedSnippet(selected.id, selected.competition_code, lang, theme, skin, 'T1', previewKey)}
          </pre>
        </div>
      )}
    </div>
  );
}

function FeedStrip({ feed, t }: { feed: import('../api/portal').FeedStatus; t: (k: string, v?: Record<string, string | number>) => string }) {
  const live = feed.live_matches[0];
  const last = feed.last_event;
  const parts: string[] = [];
  if (feed.scout_on_duty) parts.push(`${t('feed.scout')} ${feed.scout_on_duty}`);
  else if (live) parts.push(feed.live_count === 1 ? `${live.home} vs ${live.away}` : t('feed.liveN', { n: feed.live_count }));
  else parts.push(t('feed.noLive'));
  if (last) {
    const clock = last.at ? last.at.slice(11, 16) : '';
    parts.push(`${t('feed.last')} ${last.type}${clock ? ` ${clock}` : ''} · ${last.match}`);
  }
  if (feed.qc_holds > 0) parts.push(`${t('matches.qc')} ${feed.qc_holds}`);
  if (feed.quiet.active) {
    parts.push(t('feed.quietUntil', { until: feed.quiet.until || '—', n: feed.quiet.reels_queued }));
  } else if (feed.quiet.reels_queued > 0) {
    parts.push(t('feed.morning', { n: feed.quiet.reels_queued }));
  }
  return (
    <div className={clsx(
      'mb-4 flex flex-wrap items-center gap-2 rounded-xl px-3.5 py-2.5 text-[12px] text-slate-200 ring-1 ring-inset',
      feed.live_count ? 'bg-emerald-500/[0.06] ring-emerald-400/20' : 'bg-surface ring-white/[0.07]',
    )}>
      <span className="relative flex h-2 w-2">
        {feed.live_count > 0 && <span className="absolute inset-0 animate-ping rounded-full bg-emerald-400/60" />}
        <span className={clsx('relative h-2 w-2 rounded-full', feed.live_count ? 'bg-emerald-400' : 'bg-slate-500')} />
      </span>
      {parts.map((p, i) => (
        <span key={p}>
          {i > 0 && <span className="mx-2 text-slate-600">·</span>}
          {p}
        </span>
      ))}
    </div>
  );
}

function PortalHero({
  company, live, upcoming, sub, rounds,
}: {
  company?: string;
  live: number;
  upcoming: number;
  sub: string;
  rounds: boolean;
}) {
  const { t, locale } = useT();
  const now = new Date();
  const h = now.getHours();
  const hello = h < 12 ? t('hello.morning') : h < 18 ? t('hello.day') : t('hello.evening');
  const date = now.toLocaleDateString(locale === 'sr' ? 'sr-Latn-RS' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  const summary = live > 0
    ? t('hello.liveN', { n: live })
    : upcoming > 0
      ? t('hello.upcomingN', { n: upcoming })
      : t('hello.quiet');
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-subtle">{date}</div>
        <h1 className="mt-1 text-[22px] font-semibold tracking-[-0.02em] text-white">
          {hello}{company ? <>, <span className="text-white">{company}</span></> : null}
        </h1>
        <p className="mt-1 flex items-center gap-2 text-[13px] text-subtle">
          <span className={clsx('h-1.5 w-1.5 rounded-full', live > 0 ? 'animate-pulse bg-emerald-400' : 'bg-slate-500')} />
          {summary}
        </p>
        <p className="mt-0.5 text-[12px] text-slate-500">{sub}</p>
      </div>
      {rounds && (
        <Link to="/rounds" className="btn-secondary text-xs">{t('matches.rounds')}</Link>
      )}
    </div>
  );
}

function StatCard({
  label, hint, value, tone = 'idle', active, onClick,
}: {
  label: string;
  hint: string;
  value: number;
  tone?: 'live' | 'accent' | 'idle';
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'card group relative overflow-hidden px-4 py-3 text-left transition hover:-translate-y-px',
        tone === 'live' && 'bg-gradient-to-br from-emerald-500/[0.1] to-transparent',
        tone === 'accent' && 'bg-gradient-to-br from-accent/[0.08] to-transparent',
        active ? 'ring-accent/50' : 'hover:ring-white/[0.15]',
      )}
    >
      <div className="flex items-center gap-2 text-[12px] font-medium text-subtle">
        {tone === 'live' && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />}
        {label}
      </div>
      <div className={clsx(
        'mt-1.5 font-mono text-[26px] font-semibold leading-none tabular-nums',
        tone === 'live' ? 'text-emerald-300' : tone === 'accent' ? 'text-[#a9c6ff]' : 'text-white',
      )}>{value}</div>
      <div className="mt-1 text-[11px] text-slate-500">{hint}</div>
      {active && <span className="absolute inset-x-4 bottom-0 h-[2px] rounded-t-full bg-accent" />}
    </button>
  );
}

function LicenseChip({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-white/[0.04] px-2.5 py-0.5 ring-1 ring-inset ring-white/[0.08]">
      <span className="text-subtle">{label}</span>
      <span className="truncate font-medium text-slate-200">{value}</span>
    </span>
  );
}

function PublishBtn({ id, holds, t }: { id: string; holds: number; t: (k: string) => string }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => publishOfficial(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-matches'] });
      qc.invalidateQueries({ queryKey: ['portal-feed-status'] });
    },
  });
  return (
    <button
      type="button"
      className="btn-ghost text-xs"
      disabled={holds > 0 || mut.isPending}
      title={holds > 0 ? `${holds} QC holds still open` : 'Stamp official after FT + QC'}
      onClick={() => mut.mutate()}
    >
      {mut.isPending ? t('matches.publishing') : t('matches.publish')}
    </button>
  );
}
